import { useState } from "react";
import ScheduleEditor from "./ScheduleEditor";
import { formatDate, formatEuro, percentageChange } from "../../utils/timesheets";

const inputClass =
  "w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const labelClass = "block text-xs font-medium text-gray-600 mb-1";

function dayBefore(iso) {
  const date = new Date(`${iso}T00:00:00`);
  date.setDate(date.getDate() - 1);
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${m}-${d}`;
}

/**
 * "Verleng met nieuw tarief" (of nieuw rooster): de huidige lijn eindigt de dag vóór de
 * nieuwe begindatum; persoon, arts en omschrijving worden overgenomen.
 */
export default function ExtendLineModal({ line, saving, onSubmit, onCancel }) {
  const isSalaried = line.compensation_type === "salaried";
  const [newStart, setNewStart] = useState("");
  const [rate, setRate] = useState("");
  const [schedule, setSchedule] = useState(line.schedule);
  const [endDate, setEndDate] = useState("");
  const [error, setError] = useState("");

  const change = !isSalaried && rate !== "" ? percentageChange(line.rate, rate) : null;

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (!newStart) return setError("Geef de nieuwe begindatum in.");
    if (newStart <= line.start_date) return setError("De nieuwe begindatum moet na de begindatum van de huidige lijn liggen.");
    if (!isSalaried && rate === "") return setError("Geef het nieuwe tarief in.");
    onSubmit({
      new_start_date: newStart,
      rate: isSalaried ? null : Number(rate),
      schedule: isSalaried ? schedule : null,
      end_date: endDate || null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-40 overflow-y-auto p-4">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-xl my-8 p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-1">
          {isSalaried ? "Nieuw rooster" : "Verleng met nieuw tarief"}
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          {line.member_naam} — {line.description}
          {!isSalaried && <> ({formatEuro(line.rate)}, vanaf {formatDate(line.start_date)})</>}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Nieuwe begindatum</label>
            <input type="date" value={newStart} onChange={(e) => setNewStart(e.target.value)} className={inputClass} />
          </div>
          {!isSalaried && (
            <div>
              <label className={labelClass}>Nieuw tarief (€)</label>
              <input type="number" step="0.01" min="0" value={rate} onChange={(e) => setRate(e.target.value)} className={inputClass} />
            </div>
          )}
          <div>
            <label className={labelClass}>Einddatum nieuwe lijn (optioneel)</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={inputClass} />
          </div>
        </div>

        {isSalaried && (
          <div className="mt-4">
            <ScheduleEditor value={schedule} onChange={setSchedule} />
          </div>
        )}

        {newStart && newStart > line.start_date && (
          <p className="mt-4 text-sm text-gray-600">
            De huidige lijn eindigt op <strong>{formatDate(dayBefore(newStart))}</strong>.
            {change !== null && (
              <> Het tarief verandert met <strong>{change > 0 ? "+" : ""}{String(change).replace(".", ",")}%</strong>.</>
            )}
          </p>
        )}
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50">
            Annuleren
          </button>
          <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
            {saving ? "Opslaan..." : "Nieuwe lijn aanmaken"}
          </button>
        </div>
      </form>
    </div>
  );
}
